import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import BlogIcon from "@mui/icons-material/Article";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import BuildIcon from "@mui/icons-material/Build";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PeopleIcon from "@mui/icons-material/People";
import CellTowerIcon from "@mui/icons-material/CellTower";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LogoutIcon from "@mui/icons-material/Logout";
import { setLoginDetails } from "../../store/AdminStore/action";
import { useSelector, useDispatch } from "react-redux";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Collapse } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import Onit from "../ServicesStatus/onitLogo.png";
import AccessibilityIcon from "@mui/icons-material/Accessibility";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Menu, MenuItem } from "@mui/material";
import AddHomeIcon from "@mui/icons-material/AddHome";
import ElectricRickshawIcon from "@mui/icons-material/ElectricRickshaw";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  canViewDashboard,
  canViewServices,
  canViewNotifications,
  canViewCenter,
  canAddCenter,
  canViewTickets,
  canViewPendingTickets,
  canViewClosedTickets,
  canViewNoServiceTickets,
  canViewCenterTickets,
  canViewERickshawTickets,
  canAddTicket,
  canViewTechnicians,
  canViewClients,
  canViewRoles,
  canViewUsers,
  canViewCenterOnboarders,
  canViewWallet,
  canViewBanner,
  canViewOffers,
  canViewSubscription,
  canViewBlog,
} from "../../utility/permissionUtils";

const drawerWidth = 240;

const baseDrawerItems = [
  {
    name: "Home",
    path: "/Home",
    icon: <HomeIcon />,
    permission: (permissions) => canViewDashboard(permissions),
  },
  {
    name: "Service-Category",
    path: "/admin-dashboard",
    icon: <BuildIcon />,
    permission: (permissions) => canViewServices(permissions),
  },
  {
    name: "Notifications",
    path: "/notifications",
    icon: <NotificationsIcon />,
    permission: (permissions) => canViewNotifications(permissions),
  },
  {
    name: "Centers",
    path: "/admin-centers",
    icon: <SupervisorAccountIcon />,
    permission: (permissions) => canViewCenter(permissions),
    children: [
      {
        name: "All Centers",
        path: "/admin-centers",
        icon: <SupervisorAccountIcon />,
        permission: (permissions) => canViewCenter(permissions),
      },
      {
        name: "Add center",
        path: "/add-center",
        icon: <PersonAddIcon />,
        permission: (permissions) => canAddCenter(permissions),
      },
    ],
  },
  {
    name: "Request",
    icon: <PersonAddIcon />,
    permission: (permissions) => canViewTickets(permissions),
    children: [
      {
        name: "All Request",
        path: "/Tickets",
        icon: <PersonAddIcon />,
        permission: (permissions) => canViewTickets(permissions),
      },
      {
        name: "Pending Request",
        path: "/pending-ticket",
        icon: <AccessibilityIcon />,
        permission: (permissions) => canViewPendingTickets(permissions),
      },
      {
        name: "Closed Request",
        path: "/close-ticket",
        icon: <DoneAllIcon />,
        permission: (permissions) => canViewClosedTickets(permissions),
      },
      {
        name: "No Service Available",
        path: "/noService-ticket",
        icon: <LocationOffIcon />,
        permission: (permissions) => canViewNoServiceTickets(permissions),
      },
      {
        name: "Center Request",
        path: "/center-ticket",
        icon: <AddHomeIcon />,
        permission: (permissions) => canViewCenterTickets(permissions),
      },
      {
        name: "E-Rickshaw Request",
        path: "/erickshaw-ticket",
        icon: <ElectricRickshawIcon />,
        permission: (permissions) => canViewERickshawTickets(permissions),
      },
    ],
  },
  {
    name: "Add Request",
    path: "/admin-ticket",
    icon: <RequestPageIcon />,
    permission: (permissions) => canAddTicket(permissions),
  },
  {
    name: "Technicians",
    path: "/admin-technician",
    icon: <EditIcon />,
    permission: (permissions) => canViewTechnicians(permissions),
  },
  {
    name: "B2B DETAILS",
    icon: <PeopleAltIcon />,
    // Parent should be visible if any of its children are visible.
    // Operations roles may not have client-list permission but can still add/view client requests via ticket permissions.
    permission: (permissions) =>
      canViewClients(permissions) ||
      canViewTickets(permissions) ||
      canAddTicket(permissions),
    children: [
      {
        name: "Clients",
        path: "/admin-clients",
        icon: <NoteAddIcon />,
        permission: (permissions) => canViewClients(permissions),
      },
      {
        name: "All Client Requests",
        path: "/all-client-ticket",
        icon: <PersonAddIcon />,
        permission: (permissions) => canViewTickets(permissions),
      },
      {
        name: "Add Client Request",
        path: "/add-client-ticket",
        icon: <RequestPageIcon />,
        permission: (permissions) => canAddTicket(permissions),
      },
    ],
  },
  {
    name: "Pull Back",
    path: "/pullback",
    icon: <CancelIcon />,
    // permission: (permissions) => canViewTickets(permissions),
  },
  {
    name: "Roles",
    path: "/roles",
    icon: <PeopleIcon />,
    permission: (permissions) => canViewRoles(permissions),
  },
  {
    name: "Users",
    path: "/users",
    icon: <CellTowerIcon />,
    permission: (permissions) => canViewUsers(permissions),
  },
  {
    name: "Users Data",
    path: "/usersdetails",
    icon: <AdminPanelSettingsIcon />,
    permission: (permissions) => canViewUsers(permissions),
  },
  {
    name: "Center Onboarders",
    path: "/admin-center-onboarders",
    icon: <FolderSharedIcon />,
    permission: (permissions) => canViewCenterOnboarders(permissions),
  },
  {
    name: "Wallet Transaction",
    path: "/wallet",
    icon: <AccountBalanceWalletIcon />,
    permission: (permissions) => canViewWallet(permissions),
  },
  {
    name: "Banner",
    path: "/banner",
    icon: <ViewCarouselIcon />,
    permission: (permissions) => canViewBanner(permissions),
  },
  {
    name: "Offers",
    path: "/offers",
    icon: <LocalOfferIcon />,
    permission: (permissions) => canViewOffers(permissions),
  },
  {
    name: "Subscription",
    path: "/subscription",
    icon: <SubscriptionsIcon />,
    permission: (permissions) => canViewSubscription(permissions),
  },
  {
    name: "Blog",
    path: "/blog",
    icon: <BlogIcon />,
    permission: (permissions) => canViewBlog(permissions),
  },
];

function ResponsiveDrawer(props) {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openItem, setOpenItem] = React.useState("");
  const [userName, setUserName] = useState("");
  const { adminDetails } = useSelector((state) => state.userReducer);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedPage, setSelectedPage] = React.useState("");
  const [anchorEl, setAnchorEl] = useState(null);

  // Enhanced permission filtering function
  const getFilteredDrawerItems = () => {
    // Check if permissions exist
    if (!adminDetails?.role_id?.permissions) {
      console.warn("No permissions found in adminDetails");
      return [];
    }

    const userPermissions = adminDetails.role_id.permissions;

    return baseDrawerItems.filter((item) => {
      // Check if item has permission requirement and user has that permission
      const hasPermission = item.permission
        ? item.permission(userPermissions)
        : true;

      // If item doesn't have permission, skip it
      if (!hasPermission) {
        return false;
      }

      // For items with children, filter children and only show if at least one child is visible
      if (item.children) {
        const filteredChildren = item.children.filter(
          (child) => !child.permission || child.permission(userPermissions)
        );

        // Only show parent if it has at least one visible child
        return filteredChildren.length > 0;
      }

      return true;
    });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    dispatch(setLoginDetails({}));
    navigate("/admin-login");
    handleMenuClose();
  };

  useEffect(() => {
    const path = location.pathname;
    setSelectedPage(path);
  }, [location.pathname]);

  const handleToggle = (itemName) => {
    setOpenItem(openItem === itemName ? "" : itemName);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawerItems = getFilteredDrawerItems();

  // Debug: Log filtered items
  useEffect(() => {
    console.log("Admin Permissions:", adminDetails?.role_id?.permissions);
  }, [drawerItems, adminDetails]);

  const drawer = (
    <div style={{ backgroundColor: "#A3E4D7", height: "100vh" }}>
      <img
        src={Onit}
        alt="Onit Logo"
        style={{
          width: "70%",
          height: "70px",
          marginLeft: "15px",
          marginRight: "15px",
          marginTop: "10px",
        }}
      />
      <List
        sx={{
          backgroundColor: "#A3E4D7",
          "& .MuiListItemText-primary": {
            color: "black",
          },
        }}
      >
        {drawerItems.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No menu items available"
              sx={{ textAlign: "center", color: "gray" }}
            />
          </ListItem>
        ) : (
          drawerItems.map((item, index) => (
            <React.Fragment key={index}>
              {item.children ? (
                <>
                  <ListItem
                    button
                    onClick={() => handleToggle(item.name)}
                    selected={selectedPage === item.path}
                    sx={{
                      height: 45,
                      width: "100%",
                      color: selectedPage === item.path ? "white" : "black",
                      backgroundColor:
                        selectedPage === item.path ? "#0055b3" : "#A3E4D7",
                      "&:hover": {
                        backgroundColor: "#0055b3",
                        color: "white",
                        "& .MuiListItemText-primary": {
                          color: "white",
                        },
                        "& .MuiListItemIcon-root": {
                          color: "white",
                        },
                      },
                      "& .MuiListItemIcon-root": {
                        color: selectedPage === item.path ? "white" : "black",
                      },
                      "& .MuiListItemText-primary": {
                        color: selectedPage === item.path ? "white" : "black",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: "35px", marginLeft: "9px" }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.name} />
                    {openItem === item.name ? (
                      <ExpandLess
                        style={{
                          marginRight: "10px",
                          color: selectedPage === item.path ? "white" : "black",
                        }}
                      />
                    ) : (
                      <ExpandMore
                        style={{
                          marginRight: "10px",
                          color: selectedPage === item.path ? "white" : "black",
                        }}
                      />
                    )}
                  </ListItem>
                  <Collapse
                    in={openItem === item.name}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {item.children
                        .filter(
                          (childItem) =>
                            !childItem.permission ||
                            childItem.permission(
                              adminDetails?.role_id?.permissions
                            )
                        )
                        .map((childItem, childIndex) => (
                          <ListItem
                            key={childIndex}
                            button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(childItem.path);
                              setSelectedPage(childItem.path);
                              setMobileOpen(false);
                            }}
                            sx={{
                              height: 45,
                              width: "100%",
                              pl: 4,
                              color:
                                selectedPage === childItem.path
                                  ? "white"
                                  : "black",
                              backgroundColor:
                                selectedPage === childItem.path
                                  ? "#0055b3"
                                  : "#A3E4D7",
                              "&:hover": {
                                backgroundColor: "#0055b3",
                                color: "white",
                                "& .MuiListItemText-primary": {
                                  color: "white",
                                },
                                "& .MuiListItemIcon-root": {
                                  color: "white",
                                },
                              },
                              "& .MuiListItemIcon-root": {
                                color:
                                  selectedPage === childItem.path
                                    ? "white"
                                    : "black",
                              },
                              "& .MuiListItemText-primary": {
                                color:
                                  selectedPage === childItem.path
                                    ? "white"
                                    : "black",
                              },
                            }}
                            selected={selectedPage === childItem.path}
                          >
                            <ListItemIcon
                              sx={{ minWidth: "35px", marginLeft: "9px" }}
                            >
                              {childItem.icon}
                            </ListItemIcon>
                            <ListItemText primary={childItem.name} />
                          </ListItem>
                        ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem
                  button
                  component={NavLink}
                  to={item.path}
                  onClick={() => {
                    setSelectedPage(item.path);
                    setMobileOpen(false);
                  }}
                  selected={selectedPage === item.path}
                  sx={{
                    height: 45,
                    width: "100%",
                    color: selectedPage === item.path ? "white" : "black",
                    backgroundColor:
                      selectedPage === item.path ? "#0055b3" : "#A3E4D7",
                    "&:hover": {
                      backgroundColor: "#0055b3",
                      color: "white",
                      "& .MuiListItemText-primary": {
                        color: "white",
                      },
                      "& .MuiListItemIcon-root": {
                        color: "white",
                      },
                    },
                    "& .MuiListItemIcon-root": {
                      color: selectedPage === item.path ? "white" : "black",
                    },
                    "& .MuiListItemText-primary": {
                      color: selectedPage === item.path ? "white" : "black",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "35px", marginLeft: "9px" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.name} />
                </ListItem>
              )}
            </React.Fragment>
          ))
        )}
        <Divider />
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            color: "black",
            "&:hover": {
              backgroundColor: "#0055b3",
              color: "white",
              "& .MuiListItemText-primary": {
                color: "white",
              },
              "& .MuiListItemIcon-root": {
                color: "white",
              },
            },
          }}
        >
          <ListItemIcon sx={{ color: "inherit" }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary={"Logout"} />
        </ListItem>
      </List>
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ backgroundColor: "#A3E4D7" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon sx={{ color: "black" }} />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, color: "black" }}
          >
            ONIT-SERVICES (अब हर काम बहुत आसान)
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ marginRight: "10px", color: "black" }}
          >
            <b>{adminDetails?.user_name || "User"}</b>
          </Typography>
          <IconButton
            color="inherit"
            aria-label="open menu"
            edge="end"
            onClick={handleMenuOpen}
          >
            <AccountCircleIcon fontSize="large" sx={{ color: "black" }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              style: {
                width: "200px",
              },
            }}
          >
            <MenuItem
              onClick={handleLogout}
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={"Logout"} />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
}

ResponsiveDrawer.propTypes = {
  window: PropTypes.func,
};

export default ResponsiveDrawer;
